import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { ArrowLeft, Youtube, Facebook, MessageCircle, ExternalLink, User } from "lucide-react";
import { Button } from "../components/ui/button";

export default function MayaManthanGuide() {
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuide();
  }, []);

  const fetchGuide = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/guide`);
      setGuide(response.data);
    } catch (error) {
      console.error("Error fetching guide info:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="maya-guide">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          {/* Guide Photo Placeholder */}
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-500/20 to-purple-500/20 border-2 border-amber-500/30 mx-auto mb-6 flex items-center justify-center">
            {guide?.photo_url ? (
              <img src={guide.photo_url} alt={guide.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <User className="w-16 h-16 text-amber-400/50" />
            )}
          </div>
          
          <h1 className="font-serif text-4xl text-white mb-2">{guide?.name || "Your Guide"}</h1>
          <p className="text-amber-400">{guide?.title}</p>
        </motion.div>

        {/* Philosophy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-2">Philosophy</h3>
          <p className="text-2xl font-serif text-white mb-4">{guide?.philosophy}</p>
          <p className="text-gray-400 leading-relaxed">{guide?.description}</p>
        </motion.div>

        {/* Teachings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <h3 className="text-gray-400 text-sm mb-4">Core Teachings</h3>
          <div className="space-y-3">
            {guide?.teachings?.map((teaching, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <p className="text-white">{teaching}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Connect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <h3 className="text-gray-400 text-sm">Connect</h3>
          
          {/* YouTube */}
          <a
            href={guide?.youtube_channel || "https://www.youtube.com/@MayaManthan555"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-red-600/10 hover:bg-red-600/20 border border-red-600/30 rounded-xl p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Youtube className="w-6 h-6 text-red-500" />
              <div>
                <p className="text-white">YouTube Channel</p>
                <p className="text-gray-500 text-sm">@MayaManthan555</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500" />
          </a>

          {/* Facebook */}
          <a
            href={guide?.facebook_page || "https://www.facebook.com/MayaManthan.in"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-blue-600/10 hover:bg-blue-600/20 border border-blue-600/30 rounded-xl p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Facebook className="w-6 h-6 text-blue-500" />
              <div>
                <p className="text-white">Facebook Page</p>
                <p className="text-gray-500 text-sm">MayaManthan.in</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500" />
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/message/your-whatsapp-link"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-green-600/10 hover:bg-green-600/20 border border-green-600/30 rounded-xl p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <MessageCircle className="w-6 h-6 text-green-500" />
              <div>
                <p className="text-white">WhatsApp</p>
                <p className="text-gray-500 text-sm">Message for guidance</p>
              </div>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-500" />
          </a>
        </motion.div>

        {/* Intro Video Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 text-center"
        >
          <h3 className="text-gray-400 text-sm mb-4">Introduction Video</h3>
          {guide?.intro_video_url ? (
            <iframe
              src={guide.intro_video_url}
              className="w-full aspect-video rounded-xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="aspect-video bg-white/5 rounded-xl flex items-center justify-center">
              <p className="text-gray-600">Video coming soon</p>
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-gray-400 mb-4">Ready to begin your transformation?</p>
          <Button
            onClick={() => navigate('/maya-manthan/courses')}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            Explore Programs
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
